import { useQuery } from '@tanstack/react-query'
import {
  fetchFilesActivityReportExtras,
  fetchFilesContracts,
  fetchFilesDeboursNotes,
  fetchFilesExpenses,
  fetchFilesInvoices,
  fetchFilesKyc,
} from '@/apis/files'
import { useApiReady } from '@/hooks/useApiReady'

export const filesKeys = {
  all: ['files'] as const,
  contracts: (filters: Record<string, unknown>) => [...filesKeys.all, 'contracts', filters] as const,
  invoices: (filters: Record<string, unknown>) => [...filesKeys.all, 'invoices', filters] as const,
  expenses: (filters: Record<string, unknown>) => [...filesKeys.all, 'expenses', filters] as const,
  deboursNotes: (filters: Record<string, unknown>) =>
    [...filesKeys.all, 'deboursNotes', filters] as const,
  kyc: (teamId: string, filters: Record<string, unknown>) =>
    [...filesKeys.all, 'kyc', teamId, filters] as const,
}

interface FilesContractsFilters {
  page?: number
  per_page?: number
  search?: string
  status?: string
  contract_type?: string
  freelancer_id?: string
  /** Comma-separated team unit UUIDs */
  team_unit_ids?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
  hide_superseded_master_agreements?: boolean
}

interface FilesInvoicesFilters {
  page?: number
  per_page?: number
  search?: string
  freelancer_id?: string
  team_unit_ids?: string
  type?: string
  status?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

export interface FilesExpensesFilters {
  page?: number
  per_page?: number
  search?: string
  freelancer_id?: string
  team_unit_ids?: string
  expense_type?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

export interface FilesDeboursNotesFilters {
  page?: number
  per_page?: number
  search?: string
  freelancer_id?: string
  team_unit_ids?: string
  status?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

export interface FilesDeboursNoteListItem {
  id: string
  activity_report_id: string
  file_name: string
  report_month: string
  mission_id: string
  mission_name: string
  freelancer_id: string
  freelancer_name: string
  currency: string
  amount_excl_vat: number
  vat_total: number
  amount_incl_vat: number
  status: 'pending' | 'paid'
  date: string
}

export interface FilesExpenseListItem {
  id: string
  activity_report_id: string
  report_month: string
  mission_id: string
  mission_name: string
  freelancer_id: string
  freelancer_name: string
  currency: string
  date: string
  type: string
  description: string
  amount_excl_vat: number
  amount_incl_vat: number | null
  vat_rate: number | null
  vat_amount: number | null
  billing_mode: string
  vat_recoverable: boolean
  receipt_key: string
  receipt_url: string | null
  receipt_file_name: string | null
}

export interface FilesKycFilters {
  team_id: string
  page?: number
  per_page?: number
  search?: string
  category?: string | null
  freelancer_id?: string | null
  team_unit_ids?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

export interface FilesKycListItem {
  id: string
  freelancer_id: string
  freelancer_name: string
  category: string
  file_name: string
  file_key: string
  file_url: string | null
  created_at: string
  /** YYYY-MM-DD when applicable (vigilance / RC Pro), else null */
  expires_at: string | null
}

export type FilesCraExtraKind = 'feedback' | 'carbon'

export interface FilesActivityReportExtrasFilters {
  kind: FilesCraExtraKind
  page?: number
  per_page?: number
  search?: string
  freelancer_id?: string
  team_unit_ids?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

export interface FilesActivityReportExtraRow {
  id: string
  mission_id: string
  mission_name: string
  report_month: string
  status: string
  freelancer_first_name?: string
  freelancer_last_name?: string
  freelancer_id?: string | null
  mission_feedback?: string | null
  transportation_mode?: string | null
  one_way_distance_km?: number | string | null
  trips_per_week?: number | string | null
}

/** Utilisé par la page Fichiers (export CSV paginé) et par le hook. */
export async function fetchFilesActivityReportExtrasParsed(
  filters: FilesActivityReportExtrasFilters
): Promise<{
  items: FilesActivityReportExtraRow[]
  total: number
  per_page: number
  current_page: number
}> {
  const params: Record<string, string | number> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 10,
    cra_extra: filters.kind,
  }
  if (filters.search) params.search = filters.search
  if (filters.freelancer_id) params.freelancer_id = filters.freelancer_id
  if (filters.team_unit_ids) params.team_unit_ids = filters.team_unit_ids
  if (filters.sort_by) params.sort_by = filters.sort_by
  if (filters.sort_direction) params.sort_direction = filters.sort_direction

  const res = (await fetchFilesActivityReportExtras(params)) as {
    code?: number
    data?: {
      items?: FilesActivityReportExtraRow[]
      total?: number
      per_page?: number
      current_page?: number
    }
  }
  const payload = res?.data ?? {}
  return {
    items: payload.items ?? [],
    total: payload.total ?? 0,
    per_page: payload.per_page ?? 10,
    current_page: payload.current_page ?? 1,
  }
}

/**
 * Liste CRA avec champs mission_feedback ou carbone (queryKey inclut teamId pour l’isolation cache).
 */
export function useFilesActivityReportExtras(
  filters: FilesActivityReportExtrasFilters,
  options?: { enabled?: boolean; teamId?: string | null }
) {
  const apiReady = useApiReady()
  const teamId = options?.teamId ?? ''
  return useQuery({
    queryKey: [
      ...filesKeys.all,
      'activityReportExtras',
      teamId,
      filters.kind,
      filters.page ?? 1,
      filters.per_page ?? 10,
      filters.search ?? '',
      filters.freelancer_id ?? '',
      filters.team_unit_ids ?? '',
      filters.sort_by ?? '',
      filters.sort_direction ?? '',
    ] as const,
    queryFn: () => fetchFilesActivityReportExtrasParsed(filters),
    enabled: apiReady && options?.enabled !== false,
  })
}

export function useFilesContracts(filters: FilesContractsFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: filesKeys.contracts(filters),
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 10,
      }
      if (filters.search) params.search = filters.search
      if (filters.status) params.status = filters.status
      if (filters.contract_type) params.contract_type = filters.contract_type
      if (filters.freelancer_id) params.freelancer_id = filters.freelancer_id
      if (filters.team_unit_ids) params.team_unit_ids = filters.team_unit_ids
      if (filters.sort_by) params.sort_by = filters.sort_by
      if (filters.sort_direction) params.sort_direction = filters.sort_direction
      if (filters.hide_superseded_master_agreements) {
        params.hide_superseded_master_agreements = 'true'
      }

      const res = await fetchFilesContracts(params)
      const payload = (res as { code?: number; data?: { items?: unknown[]; total?: number; per_page?: number; current_page?: number } })?.data
      return {
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        per_page: payload?.per_page ?? 10,
        current_page: payload?.current_page ?? 1,
      }
    },
    enabled: apiReady && options?.enabled !== false,
  })
}

export function useFilesKyc(filters: FilesKycFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()
  const { team_id, ...rest } = filters
  return useQuery({
    queryKey: filesKeys.kyc(team_id, rest as Record<string, unknown>),
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        team_id,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 10,
      }
      if (filters.search) params.search = filters.search
      if (filters.category) params.category = filters.category
      if (filters.freelancer_id) params.freelancer_id = filters.freelancer_id
      if (filters.team_unit_ids) params.team_unit_ids = filters.team_unit_ids
      if (filters.sort_by) params.sort_by = filters.sort_by
      if (filters.sort_direction) params.sort_direction = filters.sort_direction

      const res = await fetchFilesKyc(params)
      const payload = (
        res as {
          code?: number
          data?: {
            items?: FilesKycListItem[]
            total?: number
            per_page?: number
            current_page?: number
          }
        }
      )?.data
      return {
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        per_page: payload?.per_page ?? 10,
        current_page: payload?.current_page ?? 1,
      }
    },
    enabled: apiReady && options?.enabled !== false && !!team_id,
  })
}

export function useFilesExpenses(filters: FilesExpensesFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: filesKeys.expenses(filters),
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 10,
      }
      if (filters.search) params.search = filters.search
      if (filters.freelancer_id) params.freelancer_id = filters.freelancer_id
      if (filters.team_unit_ids) params.team_unit_ids = filters.team_unit_ids
      if (filters.expense_type) params.expense_type = filters.expense_type
      if (filters.sort_by) params.sort_by = filters.sort_by
      if (filters.sort_direction) params.sort_direction = filters.sort_direction

      const res = await fetchFilesExpenses(params)
      const payload = (
        res as {
          code?: number
          data?: {
            items?: FilesExpenseListItem[]
            total?: number
            per_page?: number
            current_page?: number
          }
        }
      )?.data
      return {
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        per_page: payload?.per_page ?? 10,
        current_page: payload?.current_page ?? 1,
      }
    },
    enabled: apiReady && options?.enabled !== false,
  })
}

export function useFilesDeboursNotes(
  filters: FilesDeboursNotesFilters,
  options?: { enabled?: boolean; refetchOnMount?: boolean | 'always' },
) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: filesKeys.deboursNotes(filters),
    refetchOnMount: options?.refetchOnMount,
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 10,
      }
      if (filters.search) params.search = filters.search
      if (filters.freelancer_id) params.freelancer_id = filters.freelancer_id
      if (filters.team_unit_ids) params.team_unit_ids = filters.team_unit_ids
      if (filters.status) params.status = filters.status
      if (filters.sort_by) params.sort_by = filters.sort_by
      if (filters.sort_direction) params.sort_direction = filters.sort_direction

      const res = await fetchFilesDeboursNotes(params)
      const payload = (
        res as {
          code?: number
          data?: {
            items?: FilesDeboursNoteListItem[]
            total?: number
            per_page?: number
            current_page?: number
          }
        }
      )?.data
      return {
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        per_page: payload?.per_page ?? 10,
        current_page: payload?.current_page ?? 1,
      }
    },
    enabled: apiReady && options?.enabled !== false,
  })
}

export function useFilesInvoices(filters: FilesInvoicesFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: filesKeys.invoices(filters),
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 10,
      }
      if (filters.search) params.search = filters.search
      if (filters.freelancer_id) params.freelancer_id = filters.freelancer_id
      if (filters.team_unit_ids) params.team_unit_ids = filters.team_unit_ids
      if (filters.type) params.type = filters.type
      if (filters.status) params.status = filters.status
      if (filters.sort_by) params.sort_by = filters.sort_by
      if (filters.sort_direction) params.sort_direction = filters.sort_direction

      const res = await fetchFilesInvoices(params)
      const payload = (res as { code?: number; data?: { items?: unknown[]; total?: number; per_page?: number; current_page?: number } })?.data
      return {
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        per_page: payload?.per_page ?? 10,
        current_page: payload?.current_page ?? 1,
      }
    },
    enabled: apiReady && options?.enabled !== false,
  })
}
